import { customElement, property, state } from 'lit/decorators.js'
import { css, CSSResultGroup, html, LitElement, nothing } from 'lit'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { consume } from '@lit/context'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import {
  activeQuotationContext,
  createQuotationContext,
  createQuotationLineContext,
  getOpenQuotationContext,
  NotFetchedYet,
  quotationActionErrorContext
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import { NotFoundError, QuotationError } from 'components/ordering/quotation/api/dtos/error.js'
import { isQuotationDTO } from 'components/ordering/quotation/components/is-quotation-dto.js'
import { CSCQuotationRequestSlideIn } from 'components/ordering/quotation/components/request/slide-in.js'
import { showToast } from 'components/shared/toast-host/toast-host.js'
import { QuotationUpdateEvent } from 'components/ordering/quotation/components/quotation-update-event.js'

initializeIconsByRole({
  'csc-quotation-add-product-icon-button.icon': { name: 'file-invoice', style: 'light' },
  'csc-quotation-add-product-link.icon': { name: 'plus', style: 'light' },
  'csc-quotation-add-product-button.icon': { name: 'plus', style: 'light' },
})

export type AddProductVariant = 'button' | 'icon-button' | 'link'
export type ButtonVariant = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
export type IconButtonVariant = 'primary' | 'secondary' | 'ghost'
export type LinkVariant = 'primary' | 'secondary' | 'white'

export const fitVariants = ['auto', 'fill', 'square'] as const
export type FitVariant = typeof fitVariants[number]

// Only the button variant has a width to size. fill keeps the full width of the parent;
// auto/square shrink to the button, so a centered parent (e.g. a product tile zone) can center it.
const style = css`
  :host(:not([variant='icon-button']):not([variant='link'])[fit='fill']) {
    display: block;
    width: 100%;
  }

  :host(:not([variant='icon-button']):not([variant='link'])[fit='auto']),
  :host(:not([variant='icon-button']):not([variant='link'])[fit='square']) {
    display: block;
    width: fit-content;
  }
`

@customElement('csc-quotation-add-product')
export class CSCQuotationAddProduct extends LitElement {
  static override styles: CSSResultGroup = [style]

  @consume({ context: activeQuotationContext, subscribe: true })
  private quotation!: QuotationDTO | NotFoundError | NotFetchedYet

  @consume({ context: getOpenQuotationContext })
  private getOpenQuotation!: () => Promise<void>

  @consume({ context: createQuotationContext })
  private createQuotation!: () => Promise<void>

  @consume({ context: createQuotationLineContext })
  private createQuotationLine!: (quotationId: number, productId: number) => Promise<QuotationDTO>

  @consume({ context: quotationActionErrorContext, subscribe: true })
  private actionError!: QuotationError | null

  @property({ type: Number, attribute: 'product-id' })
  productId!: number

  @property({ type: String, reflect: true })
  variant: AddProductVariant = 'button'

  @property({ type: String, attribute: 'button-variant' })
  buttonVariant: ButtonVariant = 'default'

  @property({ type: String, attribute: 'icon-variant' })
  iconVariant: IconButtonVariant = 'primary'

  @property({ type: String, attribute: 'link-variant' })
  linkVariant: LinkVariant = 'primary'

  @property({ type: String, reflect: true })
  fit: FitVariant = 'fill'

  @state()
  private isLoading: boolean = false

  private get label() {
    return t('add_product', { ns: 'csc-quotation-add-product', default: 'Request quote' })
  }

  private get buttonPrefix() {
    if (this.buttonVariant !== 'primary') return nothing
    return this.isLoading
      ? html`<cse-loader slot="prefix"></cse-loader>`
      : html`<cse-icon icon-role="csc-quotation-add-product-button.icon" slot="prefix"></cse-icon>`
  }

  private async addProduct() {
    this.isLoading = true

    try {
      if (!isQuotationDTO(this.quotation)) await this.createQuotation()
      if (this.actionError) throw this.actionError
      if (isQuotationDTO(this.quotation)) {
        await this.createQuotationLine(this.quotation.id, this.productId)
        if (this.actionError) throw this.actionError
        await this.productAddedEvent()
        await this.openSlideIn()
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error)
      showToast({ variant: 'error', message })
      await this.openSlideIn()
      return
    } finally {
      this.isLoading = false
    }
  }

  private async productAddedEvent() {
    window.dispatchEvent(new CustomEvent(QuotationUpdateEvent, {
      detail: {
        quotation: this.quotation
      },
      bubbles: true,
      composed: true
    }))
  }

  private async openSlideIn() {
    const slideIn: CSCQuotationRequestSlideIn = await new CSCQuotationRequestSlideIn().createSlideIn() as CSCQuotationRequestSlideIn
    slideIn.open()
  }

  override async updated() {
    if (!isQuotationDTO(this.quotation)) {
      await this.getOpenQuotation?.()
    }
  }

  override render() {
    if (this.variant === 'icon-button') {
      return html`
        <cse-icon-button variant=${this.iconVariant} @click=${this.addProduct}>
          <cse-icon icon-role="csc-quotation-add-product-icon-button.icon"></cse-icon>
        </cse-icon-button>
      `
    }

    if (this.variant === 'link') {
      return html`
        <cse-link variant=${this.linkVariant} @click=${this.addProduct}>
          <cse-icon icon-role="csc-quotation-add-product-link.icon" slot="prefix"></cse-icon>
          ${this.label}
        </cse-link>
      `
    }

    return html`
      <cse-button
        variant=${this.buttonVariant}
        ?disabled=${this.isLoading}
        fit=${this.fit}
        @click=${this.addProduct}
      >
        ${this.buttonPrefix}
        ${this.label}
      </cse-button>
    `
  }
}
